import React, { useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    MenuItem,
    Grid,
} from '@mui/material';
import { IAgent } from '../../definitions/app/agent.d';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FormProvider } from '../hook-form';

interface EditAgentModalProps {
    open: boolean;
    onClose: () => void;
    agent: IAgent | null;
    onUpdate: (updatedAgent: IAgent) => void;
}

const EditAgentModal: React.FC<EditAgentModalProps> = ({ open, onClose, agent, onUpdate }) => {

    const EditAgentSchema = Yup.object().shape({
        name: Yup.string().required('Name is required'),
        mobile: Yup.string().matches(/^[0-9]{10}$/, 'Mobile number is not valid').required('Mobile number is required'),
        address: Yup.string().required('Address is required'),
        active: Yup.boolean().required('Active status is required'),
        fare: Yup.number().typeError('Fare must be a number').required('Fare is required').min(0, 'Fare cannot be negative'),
        kg14_refill: Yup.number().typeError('14KG Refilling must be a number').required('14KG Refilling is required').min(0, '14KG Refilling cannot be negative'),
        kg14_nc: Yup.number().typeError('14KG NC must be a number').required('14KG NC is required').min(0, '14KG NC cannot be negative'),
        kg14_dbc: Yup.number().typeError('14KG DBC must be a number').required('14KG DBC is required').min(0, '14KG DBC cannot be negative'),
        kg19_refill: Yup.number().typeError('19KG Refilling must be a number').required('19KG Refilling is required').min(0, '19KG Refilling cannot be negative'),
        kg19_nc: Yup.number().typeError('19KG NC must be a number').required('19KG NC is required').min(0, '19KG NC cannot be negative'),
        kg5_ftl_refill: Yup.number().typeError('5KG FTL Refilling must be a number').required('5KG FTL Refilling is required').min(0, '5KG FTL Refilling cannot be negative'),
        kg5_ftl_nc: Yup.number().typeError('5KG FTL NC must be a number').required('5KG FTL NC is required').min(0, '5KG FTL NC cannot be negative'),
        kg5_domestic: Yup.number().typeError('5KG Domestic must be a number').required('5KG Domestic is required').min(0, '5KG Domestic cannot be negative'),
    });

    const methods = useForm<IAgent>({
        resolver: yupResolver(EditAgentSchema),
        mode: 'onTouched',
    });

    const { handleSubmit, reset, control } = methods;

    useEffect(() => {
        if (agent) {
            reset(agent);
        }
    }, [agent, open, reset]);

    const onFormSubmit = (data: IAgent) => {
        const payload = { ...agent, ...data };
        onUpdate(payload);
    };


    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Edit Agent</DialogTitle>
            <DialogContent>
                <FormProvider methods={methods} onSubmit={handleSubmit(onFormSubmit)}>
                    <Grid container spacing={2} sx={{ mt: 2 }}>
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="name"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Agent Name"
                                        error={!!error}
                                        helperText={error?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="mobile"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Mobile Number"
                                        error={!!error}
                                        helperText={error?.message}
                                        disabled
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Controller
                                name="address"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Address"
                                        error={!!error}
                                        helperText={error?.message}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="fare"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="Fare"
                                        type="number"
                                        error={!!error}
                                        helperText={error?.message}
                                        value={field.value || ''}
                                        onChange={(event) => field.onChange(Number(event.target.value))}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="active"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        {...field}
                                        select
                                        fullWidth
                                        label="Status"
                                        error={!!error}
                                        helperText={error?.message}
                                        value={String(field.value)}
                                        onChange={(e) => field.onChange(e.target.value === 'true')}
                                    >
                                        <MenuItem value="true">Active</MenuItem>
                                        <MenuItem value="false">Inactive</MenuItem>
                                    </TextField>
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="kg14_refill"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="14KG Refill Rate"
                                        type="number"
                                        error={!!error}
                                        helperText={error?.message}
                                        value={field.value || ''}
                                        onChange={(event) => field.onChange(Number(event.target.value))}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="kg14_nc"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="14KG NC Rate"
                                        type="number"
                                        error={!!error}
                                        helperText={error?.message}
                                        value={field.value || ''}
                                        onChange={(event) => field.onChange(Number(event.target.value))}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="kg14_dbc"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="14KG DBC Rate"
                                        type="number"
                                        error={!!error}
                                        helperText={error?.message}
                                        value={field.value || ''}
                                        onChange={(event) => field.onChange(Number(event.target.value))}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="kg19_refill"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="19KG Refill Rate"
                                        type="number"
                                        error={!!error}
                                        helperText={error?.message}
                                        value={field.value || ''}
                                        onChange={(event) => field.onChange(Number(event.target.value))}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="kg19_nc"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="19KG NC Rate"
                                        type="number"
                                        error={!!error}
                                        helperText={error?.message}
                                        value={field.value || ''}
                                        onChange={(event) => field.onChange(Number(event.target.value))}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="kg5_ftl_refill"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="5KG FTL Refill Rate"
                                        type="number"
                                        error={!!error}
                                        helperText={error?.message}
                                        value={field.value || ''}
                                        onChange={(event) => field.onChange(Number(event.target.value))}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="kg5_ftl_nc"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="5KG FTL NC Rate"
                                        type="number"
                                        error={!!error}
                                        helperText={error?.message}
                                        value={field.value || ''}
                                        onChange={(event) => field.onChange(Number(event.target.value))}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="kg5_domestic"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label="5KG Domestic Rate"
                                        type="number"
                                        error={!!error}
                                        helperText={error?.message}
                                        value={field.value || ''}
                                        onChange={(event) => field.onChange(Number(event.target.value))}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                </FormProvider>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSubmit(onFormSubmit)} variant="contained">Save Changes</Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditAgentModal;